import { useCoreServices } from '@bangle.io/context';
import {
  ensureFreshSession,
  isSupabaseConfigured,
  onAuthStateChange,
  signOut,
} from '@bangle.io/supabase';
import { StarButton } from '@bangle.io/ui-components';
import { useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { MarkdownFidelityNotice } from '../components/navigation/markdown-fidelity-notice';
import { NoteSaveStatus } from '../components/navigation/note-save-status';
import { PresenceAvatars } from './presence-avatars';
import { SharePanel } from './share-panel';
import { removeLocalCloudWorkspaces } from './sync-cloud-workspaces';
import { useCloudWorkspaceAccess } from './use-cloud-workspace-access';

/**
 * Right-side titlebar chrome: note save status, fidelity notice, star, and
 * account controls. Kept as one inline cluster next to the email so nothing
 * is clipped by the breadcrumb / overflow-hidden header.
 */
export function CloudToolbar() {
  const core = useCoreServices();
  const routeInfo = useAtomValue(core.navigation.$routeInfo);
  const isCurrentWsPathStarred = useAtomValue(
    core.userActivityService.$isCurrentWsPathStarred,
  );
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  // Tied to the note path so navigating away closes share automatically.
  const [shareForWsPath, setShareForWsPath] = useState<string | null>(null);
  const { workspaceId, isViewer } = useCloudWorkspaceAccess();

  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    void ensureFreshSession().then((s) =>
      setSessionEmail(s?.user.email ?? null),
    );
    return onAuthStateChange((s) => setSessionEmail(s?.user.email ?? null));
  }, []);

  const currentWsPath = useMemo(() => {
    if (routeInfo.route !== 'editor') return undefined;
    const wsPath = routeInfo.payload?.wsPath;
    return typeof wsPath === 'string' ? wsPath : undefined;
  }, [routeInfo]);

  const currentNotePath = useMemo(() => {
    if (!currentWsPath) return undefined;
    const idx = currentWsPath.indexOf(':');
    return idx >= 0 ? currentWsPath.slice(idx + 1) : undefined;
  }, [currentWsPath]);

  const shareOpen = shareForWsPath !== null && shareForWsPath === currentWsPath;
  const supabaseConfigured = isSupabaseConfigured();
  const canShareNote = Boolean(workspaceId && currentNotePath && !isViewer);
  const showNoteChrome = Boolean(currentWsPath);

  if (!supabaseConfigured && !showNoteChrome) {
    return null;
  }

  return (
    <>
      <div className="sn-cloud-toolbar desktop-titlebar-no-drag">
        {currentWsPath ? (
          <>
            <NoteSaveStatus wsPath={currentWsPath} />
            <MarkdownFidelityNotice wsPath={currentWsPath} />
            <StarButton
              isStarred={isCurrentWsPathStarred}
              onClick={() => {
                core.commandDispatcher.dispatch(
                  'command::workspace:toggle-star',
                  { wsPath: currentWsPath },
                  'CloudToolbar',
                );
              }}
              disabled={isViewer}
              title={
                isCurrentWsPathStarred
                  ? t.app.common.unstarItem
                  : t.app.common.starItem
              }
            />
          </>
        ) : null}
        {supabaseConfigured ? (
          sessionEmail ? (
            <>
              <PresenceAvatars />
              <span className="sn-cloud-user" title={sessionEmail}>
                {sessionEmail}
              </span>
              {canShareNote && (
                <button
                  type="button"
                  onClick={() => setShareForWsPath(currentWsPath ?? null)}
                >
                  Share
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  void (async () => {
                    // Drop local cloud workspace rows before reload so they
                    // never flash back into the sidebar while signed out.
                    await removeLocalCloudWorkspaces(core.workspaceOps);
                    await signOut();
                    window.location.reload();
                  })();
                }}
              >
                Sign Out
              </button>
            </>
          ) : (
            <a href="/login">Sign In</a>
          )
        ) : null}
      </div>
      {workspaceId && currentNotePath && currentWsPath && (
        <SharePanel
          open={shareOpen}
          onOpenChange={(open) => {
            setShareForWsPath(open ? currentWsPath : null);
          }}
          workspaceId={workspaceId}
          currentNotePath={currentNotePath}
        />
      )}
    </>
  );
}
